import React, { useEffect, useId, useRef, useState } from "react";
import "./LineSelect.css";

function matchesSearch(label, query) {
  const words = String(label || "")
    .toLowerCase()
    .split(/[^a-z0-9+]+/)
    .filter(Boolean);
  return words.some((word) => word.startsWith(query) || word.replace(/^\+/, "").startsWith(query));
}

export default function LineSelect({
  value,
  placeholder = "Select",
  options = [],
  disabled = false,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();
  const selected = options.find((option) => option.value === value);
  const searchable = options.length > 8;
  const query = search.trim().toLowerCase();
  const visibleOptions = query
    ? options.filter((option) => matchesSearch(option.label, query))
    : options;

  useEffect(() => {
    if (!open) return undefined;
    if (searchable) searchRef.current?.focus();
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, searchable]);

  const choose = (next) => {
    onChange(next);
    setSearch("");
    setOpen(false);
  };

  return (
    <div className={`line-select${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className={`line-select__trigger${selected ? "" : " is-empty"}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        title={selected ? selected.label : undefined}
        onClick={() => {
          setSearch("");
          setOpen((current) => !current);
        }}
      >
        {selected ? selected.label : placeholder}
      </button>
      {open ? (
        <ul className="line-select__list" id={listId} role="listbox">
          {searchable ? (
            <li>
              <input
                ref={searchRef}
                className="line-select__search"
                value={search}
                placeholder="Type to search"
                aria-label="Search"
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
              />
            </li>
          ) : null}
          {query ? null : (
            <li>
              <button type="button" className="line-select__option is-placeholder" role="option" aria-selected={false} onClick={() => choose("")}>
                {placeholder}
              </button>
            </li>
          )}
          {visibleOptions.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                className={`line-select__option${option.value === value ? " is-selected" : ""}`}
                role="option"
                aria-selected={option.value === value}
                onClick={() => choose(option.value)}
              >
                {option.label}
              </button>
            </li>
          ))}
          {query && !visibleOptions.length ? (
            <li className="line-select__empty">No matches</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
